import {
  Component,
  input,
  InputSignal,
  ElementRef,
  Signal,
  effect,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { DisplayAnimations } from '../../directives/display-animations';
import { TrainingSectionType } from '../../models/training-section.model';
import Chart, { ChartDataset } from 'chart.js/auto';
import { Coordinate } from '../../models/coordinate.model';
import { ChartService } from '../../services/chart-service';
import { getTrainingSectionColor } from '../../helpers/training-section-color.helpers';
import { WebSocketService } from '../../services/websocket-service';
import { UnitType } from '../../models/unit.model';
import { ChartNextSegmentInfo } from '../chart-next-segment-info/chart-next-segment-info';
import { ChartCurrentSegmentInfo } from '../chart-current-segment-info/chart-current-segment-info';
import { TrainingModeType } from '../../models/training-mode.model';

type ElevationChartData = (number | null)[];
type ElevationChartDataset = ChartDataset<'line', ElevationChartData>[];

@Component({
  selector: 'app-screen-chart',
  imports: [ChartNextSegmentInfo, ChartCurrentSegmentInfo, NgStyle],
  template: `
    <app-chart-current-segment-info />
    <div class="chart" [ngStyle]="{ mask: maskStyle(), '-webkit-mask': maskStyle() }">
      <div class="chart-clip">
        <canvas #chartCanvas></canvas>
      </div>
    </div>
    <app-chart-next-segment-info />
  `,
  styleUrl: './screen-chart.scss',
  hostDirectives: [DisplayAnimations],
})
export class ScreenChart {
  private chartService: ChartService = inject(ChartService);
  private webSocketService: WebSocketService = inject(WebSocketService);
  trainingSections: InputSignal<TrainingSectionType[]> = input.required();
  protected chartCanvas: Signal<ElementRef<HTMLCanvasElement>> = viewChild.required('chartCanvas', {
    read: ElementRef<HTMLCanvasElement>,
  });
  protected readonly unit: UnitType = (localStorage.getItem('unit') as UnitType) || 'metric';
  private chart: Chart<'line', ElevationChartData, number> | undefined;
  protected readonly currentSlope: Signal<number> = computed<number>(
    () => this.webSocketService.broadcastData()?.data.slope || 0,
  );

  isZoomed: Signal<boolean> = computed(() => this.webSocketService.config()?.chart === 'center');

  trainingMode: Signal<TrainingModeType> = computed(
    () => this.webSocketService.contentInformation()?.trainingMode || 'originalSpeed',
  );

  private readonly closestIndexComputed: Signal<number> = computed(() => {
    const labels: number[] = this.chartLabels();
    const clientValue: number | undefined =
      this.trainingMode() === 'originalSpeed' ? this.clientTime() : this.clientDistance();
    if (!labels.length || clientValue === undefined) return 0;
    let closestIndex: number = 0;
    let minDiff: number = Infinity;
    for (let i = 0; i < labels.length; i++) {
      const diff: number = Math.abs(labels[i] - clientValue);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    return closestIndex;
  });

  protected readonly maskStyle: Signal<string> = computed(() => {
    if (!this.isZoomed()) return '';
    const labels: number[] = this.chartLabels();
    if (labels.length === 0) return '';
    const closestIdx: number = this.closestIndexComputed();
    const halfWindow: number = Math.floor(labels.length / 6);
    const from: number = Math.max(0, closestIdx - halfWindow);
    const to: number = Math.min(labels.length - 1, closestIdx + halfWindow);
    const windowSize: number = to - from;
    if (windowSize === 0) return '';
    const markerP: number = ((closestIdx - from) / windowSize) * 100;
    const leftEnd: number = Math.min(markerP, 20);
    const leftStart: number = Math.max(0, leftEnd - 17);
    const rightStart: number = Math.max(markerP, 80);
    const rightEnd: number = Math.min(100, rightStart + 17);
    return `linear-gradient(to right, transparent ${leftStart.toFixed(1)}%, black ${leftEnd.toFixed(1)}%, black ${rightStart.toFixed(1)}%, transparent ${rightEnd.toFixed(1)}%)`;
  });

  private chartLabels: Signal<number[]> = computed<number[]>(() =>
    this.trainingSections().flatMap((trainingSection: TrainingSectionType) =>
      trainingSection.coordinates.flatMap((coordinate: Coordinate) =>
        this.trainingMode() === 'originalSpeed' ? coordinate.position : coordinate.distance,
      ),
    ),
  );

  private readonly elevationsData: Signal<ElevationChartData> = computed(() =>
    this.mapElevationsBy(() => true),
  );

  private readonly elevationDataBefore: Signal<ElevationChartData> = computed(() =>
    this.mapElevationsBy(
      (coordinate) =>
        (this.trainingMode() === 'originalSpeed' ? coordinate.position : coordinate.distance) <=
        (this.trainingMode() === 'originalSpeed'
          ? this.clientTime() || 0
          : this.clientDistance() || 0),
    ),
  );

  private readonly elevationDataAfter: Signal<ElevationChartData> = computed(() =>
    this.mapElevationsBy(
      (coordinate) =>
        (this.trainingMode() === 'originalSpeed' ? coordinate.position : coordinate.distance) >
        (this.trainingMode() === 'originalSpeed'
          ? this.clientTime() || 0
          : this.clientDistance() || 0),
    ),
  );

  private readonly chartDatasets: Signal<ElevationChartDataset> = computed<ElevationChartDataset>(
    () => [
      {
        data: this.elevationDataBefore(),
        borderColor: 'rgba(0, 0, 0, 0.3)',
        borderWidth: 8,
        pointRadius: 0,
        tension: 1,
        order: 2,
      },
      {
        data: this.elevationDataAfter(),
        borderColor: 'rgba(0,0,0,1)',
        borderWidth: 8,
        pointRadius: 0,
        tension: 1,
        order: 2,
      },
      {
        data: this.elevationsData(),
        segment: {
          borderColor: this.getSegmentColor.bind(this),
        },
        borderWidth: 3,
        pointRadius: 0,
        tension: 1,
        order: 1,
      },
    ],
  );

  clientDistance: Signal<number | undefined> = computed<number | undefined>(
    () => this.webSocketService.broadcastData()?.data.distance,
  );

  clientTime: Signal<number | undefined> = computed<number | undefined>(
    () => this.webSocketService.broadcastData()?.data.time,
  );

  constructor() {
    effect(() => {
      const labels: number[] = this.chartLabels();
      const datasets: ElevationChartDataset = this.chartDatasets();
      if (!this.chart) return;
      this.chart.data.labels = labels;
      this.chart.data.datasets = datasets;
      this.updateMarkerPosition(labels, datasets);

      if (this.isZoomed() && labels.length > 0) {
        const closestIndex: number = this.closestIndexComputed();
        const halfWindow: number = Math.floor(labels.length / 6);
        const from: number = Math.max(0, closestIndex - halfWindow);
        const to: number = Math.min(labels.length - 1, closestIndex + halfWindow);
        this.chart.options.scales!['x']!.min = labels[from];
        this.chart.options.scales!['x']!.max = labels[to];
      } else {
        this.chart.options.scales!['x']!.min = undefined;
        this.chart.options.scales!['x']!.max = undefined;
      }
      this.chart.update();
    });
  }

  private mapElevationsBy(
    predicate: (coordinate: Coordinate, index: number, coordinates: Coordinate[]) => boolean,
  ): ElevationChartData {
    return this.trainingSections().flatMap((trainingSection: TrainingSectionType) => {
      const coordinates: Coordinate[] = trainingSection.coordinates;
      return coordinates.map((coordinate, index) =>
        predicate(coordinate, index, coordinates) ? coordinate.elevation : null,
      );
    });
  }

  private updateMarkerPosition(labels: number[], datasets: ElevationChartDataset): void {
    if (labels.length === 0 || datasets.length === 0) return;
    const data: ElevationChartData = datasets[2].data;
    this.chartService.startMarkerAnnotation.xValue = 0;
    this.chartService.startMarkerAnnotation.yValue = data[0] ?? 0;
    this.chartService.endMarkerAnnotation.xValue = labels.length - 1;
    this.chartService.endMarkerAnnotation.yValue = data[data.length - 1] ?? 0;
  }

  private getSegmentColor(ctx: any): string {
    const sectionIndex: number = this.getSectionIndexFromCoordinateIndex(ctx.p0DataIndex);
    const slope: number = this.trainingSections()[sectionIndex].slope;
    return getTrainingSectionColor(slope);
  }

  private getSectionIndexFromCoordinateIndex(coordinateIndex: number): number {
    let currentIndex = 0;
    for (let i = 0; i < this.trainingSections().length; i++) {
      const sectionLength: number = this.trainingSections()[i].coordinates.length;
      if (coordinateIndex < currentIndex + sectionLength) {
        return i;
      }
      currentIndex += sectionLength;
    }
    return this.trainingSections().length - 1;
  }

  ngAfterViewInit(): void {
    const labels: number[] = this.chartLabels();
    const datasets: ElevationChartDataset = this.chartDatasets();
    this.updateMarkerPosition(labels, datasets);
    this.chart = new Chart<'line', ElevationChartData, number>(this.chartCanvas().nativeElement, {
      type: 'line',
      options: this.chartService.OPTIONS,
      data: { labels, datasets },
      plugins: [this.chartService.drawClientDistanceLine(this)],
    });

    const { startMarkerImage, endMarkerImage } = this.chartService;
    if (!startMarkerImage.complete) {
      startMarkerImage.addEventListener('load', () => this.chart?.update(), { once: true });
    }
    if (!endMarkerImage.complete) {
      endMarkerImage.addEventListener('load', () => this.chart?.update(), { once: true });
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }
}
